import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLoadingState } from '../hooks';
import { LoadingPanel } from '../components/brand/TwindenLoader';
import { Link as RouterLink } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Link,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { ChevronDown, ChevronUp, Lightbulb, Pencil, Plus } from 'lucide-react';
import { IDEA_LIMITS, orderIdeas } from '@twinden/shared';
import { api, ApiError, type Idea } from '../lib/api';
import { canEditIdea, ideaSections, withVote } from '../lib/ideas';
import { formatDate } from '../lib/format';
import { isAuthenticatedAtom } from '../store';
import { Layout } from '../components/Layout';
import {
  ReadingColumn,
  SiteShell,
  richText,
} from '../components/site/SiteChrome';
import { useMonthName } from '../components/site/CostsCard';
import { IdeaSheet, type IdeaDraft } from '../components/ideas/IdeaSheet';
import { useT, type MessageKey } from '../i18n';

// Shape Twinden (M55, docs/run-by-families.md D198–D200): ideas from the
// families who use Twinden, with votes. Signed out it's read-only, in the
// public pages' frame; signed in it sits in the app. Ideas never say who
// posted them.

const STATUS: Record<
  Exclude<Idea['status'], 'open'>,
  { label: MessageKey; bg: string; color: string }
> = {
  building: {
    label: 'site.ideas.status.building',
    bg: 'accent.solid',
    color: 'accent.onSolid',
  },
  planned: {
    label: 'site.ideas.status.planned',
    bg: 'accent.tint',
    color: 'accent',
  },
  done: { label: 'site.ideas.status.done', bg: 'saffron', color: '#1F1D1B' },
  not_now: {
    label: 'site.ideas.status.notNow',
    bg: 'surface.subtle',
    color: 'muted',
  },
};

function StatusChip({ status }: { status: Idea['status'] }) {
  const t = useT();
  if (status === 'open') return null;
  const chip = STATUS[status];
  return (
    <Text
      as="span"
      display="inline-block"
      bg={chip.bg}
      color={chip.color}
      fontSize="12px"
      fontWeight={700}
      borderRadius="full"
      px={2.5}
      py={0.5}
    >
      {t(chip.label)}
    </Text>
  );
}

function VoteButton({
  idea,
  signedIn,
  busy,
  onVote,
}: {
  idea: Idea;
  signedIn: boolean;
  busy: boolean;
  onVote: () => void;
}) {
  const t = useT();
  const voted = !!idea.votedByMe;
  const count = t(
    idea.votesCount === 1 ? 'site.ideas.votesOne' : 'site.ideas.votesOther',
    { count: idea.votesCount }
  );
  // Done ideas, and signed-out readers, see the count only.
  if (!signedIn || idea.status === 'done') {
    return (
      <VStack
        spacing={0}
        minW="52px"
        color="muted"
        aria-label={count}
        role="img"
      >
        <ChevronUp size={18} aria-hidden="true" />
        <Text fontWeight={800} fontSize="15px" aria-hidden="true">
          {idea.votesCount}
        </Text>
      </VStack>
    );
  }
  return (
    <Button
      onClick={onVote}
      isDisabled={busy}
      aria-pressed={voted}
      aria-label={`${t(voted ? 'site.ideas.unvote' : 'site.ideas.vote', { title: idea.title })} (${count})`}
      variant={voted ? 'solid' : 'outline'}
      colorScheme={voted ? undefined : 'gray'}
      flexDirection="column"
      h="56px"
      minW="52px"
      px={2}
      borderRadius="16px"
      gap={0}
    >
      <ChevronUp size={18} aria-hidden="true" />
      <Text as="span" fontWeight={800} fontSize="15px" aria-hidden="true">
        {idea.votesCount}
      </Text>
    </Button>
  );
}

function IdeaCard({
  idea,
  signedIn,
  busy,
  onVote,
  onEdit,
}: {
  idea: Idea;
  signedIn: boolean;
  busy: boolean;
  onVote: () => void;
  onEdit: () => void;
}) {
  const t = useT();
  const monthName = useMonthName();
  const shipped =
    idea.status === 'done' && idea.doneAt
      ? t('site.ideas.shipped', { month: monthName(idea.doneAt.slice(0, 7)) })
      : null;
  return (
    <Box
      as="li"
      bg="surface"
      borderWidth="1px"
      borderColor="line"
      borderRadius="card"
      p={4}
    >
      <HStack align="flex-start" spacing={3}>
        <Box flex="1" minW={0}>
          {(idea.status !== 'open' || shipped) && (
            <HStack spacing={2} pb={1.5} flexWrap="wrap">
              <StatusChip status={idea.status} />
              {shipped && (
                <Text as="span" fontSize="13px" color="muted" fontWeight={600}>
                  {shipped}
                </Text>
              )}
            </HStack>
          )}
          <Heading as="h3" fontSize="17px" lineHeight="23px">
            {idea.title}
          </Heading>
          {idea.body && (
            <Text
              color="ink.body"
              fontSize="15px"
              lineHeight="21px"
              pt={1}
              whiteSpace="pre-wrap"
              overflowWrap="anywhere"
            >
              {idea.body}
            </Text>
          )}
          {idea.statusNote && (
            <Text
              fontSize="14px"
              color="ink.body"
              bg="surface.subtle"
              borderRadius="12px"
              px={3}
              py={2}
              mt={2}
            >
              {idea.statusNote}
            </Text>
          )}
          <HStack spacing={3} pt={2} flexWrap="wrap">
            <Text fontSize="13px" color="muted">
              {t('site.ideas.from', {
                date: formatDate(new Date(idea.createdAt)),
              })}
            </Text>
            {idea.mergedCount > 0 && (
              <Text fontSize="13px" color="muted">
                {t(
                  idea.mergedCount === 1
                    ? 'site.ideas.mergedOne'
                    : 'site.ideas.mergedOther',
                  { count: idea.mergedCount }
                )}
              </Text>
            )}
            {canEditIdea(idea) && (
              <Button
                size="xs"
                variant="ghost"
                color="accent"
                leftIcon={<Pencil size={13} />}
                onClick={onEdit}
              >
                {t('site.ideas.edit')}
              </Button>
            )}
          </HStack>
        </Box>
        <VoteButton
          idea={idea}
          signedIn={signedIn}
          busy={busy}
          onVote={onVote}
        />
      </HStack>
    </Box>
  );
}

function Folded({
  ideas,
  show,
  hide,
  render,
}: {
  ideas: Idea[];
  show: string;
  hide: string;
  render: (idea: Idea) => JSX.Element;
}) {
  const [open, setOpen] = useState(false);
  if (ideas.length === 0) return null;
  return (
    <Box>
      <Button
        variant="ghost"
        colorScheme="gray"
        size="sm"
        rightIcon={open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {open ? hide : show}
      </Button>
      {open && (
        <VStack as="ul" listStyleType="none" align="stretch" spacing={3} pt={2}>
          {ideas.map(render)}
        </VStack>
      )}
    </Box>
  );
}

export function IdeasBoard({ signedIn }: { signedIn: boolean }) {
  const t = useT();
  const toast = useToast();
  const [ideas, setIdeas] = useState<Idea[] | null>(null);
  const [failed, setFailed] = useState(false);
  // Only the most wanted are sent (M55 review): say so.
  const [truncated, setTruncated] = useState(false);
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [sheet, setSheet] = useState<{ editing: Idea | null } | null>(null);
  // The latest list, for putting a vote back after a failure.
  const latest = useRef<Idea[] | null>(null);
  latest.current = ideas;

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const response = await api.getIdeas();
      setIdeas(response.data.ideas);
      setTruncated(!!response.data.truncated);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, signedIn]);
  // The mark while the board loads; Try again after a failure (M66, D249).
  const ideasState = useLoadingState({
    loading: ideas === null && !failed,
    hasData: ideas !== null,
    error: failed,
    retry: () => void load(),
  });

  const replace = (idea: Idea) =>
    setIdeas((list) =>
      list ? list.map((item) => (item.id === idea.id ? idea : item)) : list
    );

  // Optimistic: the vote shows at once and goes back if the server refuses.
  const toggleVote = async (idea: Idea) => {
    const vote = !idea.votedByMe;
    setBusy((ids) => new Set(ids).add(idea.id));
    replace(withVote(idea, vote));
    try {
      const response = await api.voteIdea(idea.id, vote);
      const current = latest.current?.find((item) => item.id === idea.id);
      if (current) {
        replace({
          ...current,
          votesCount: response.data.votesCount,
          votedByMe: response.data.votedByMe,
        });
      }
    } catch (err) {
      replace(idea);
      toast({
        title: t('site.ideas.voteFailed'),
        description:
          err instanceof ApiError ? err.message : 'Something went wrong',
        status: 'error',
      });
    } finally {
      setBusy((ids) => {
        const next = new Set(ids);
        next.delete(idea.id);
        return next;
      });
    }
  };

  const editing = sheet?.editing ?? null;
  const initial = useMemo<IdeaDraft | null>(
    () => (editing ? { title: editing.title, body: editing.body ?? '' } : null),
    [editing]
  );

  const save = async (draft: IdeaDraft) => {
    try {
      if (editing) {
        const response = await api.editIdea(editing.id, {
          title: draft.title,
          body: draft.body || null,
        });
        replace(response.data.idea);
        toast({ title: t('site.ideas.saved'), status: 'success' });
      } else {
        const response = await api.postIdea({
          title: draft.title,
          body: draft.body || null,
        });
        setIdeas((list) => orderIdeas([...(list ?? []), response.data.idea]));
        toast({ title: t('site.ideas.posted'), status: 'success' });
      }
      setSheet(null);
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : 'Something went wrong',
        status: 'error',
      });
      throw err;
    }
  };

  const sections = ideas ? ideaSections(ideas) : null;
  const card = (idea: Idea) => (
    <IdeaCard
      key={idea.id}
      idea={idea}
      signedIn={signedIn}
      busy={busy.has(idea.id)}
      onVote={() => void toggleVote(idea)}
      onEdit={() => setSheet({ editing: idea })}
    />
  );

  return (
    <VStack align="stretch" spacing={6}>
      <VStack align="flex-start" spacing={3}>
        <Flex
          w="44px"
          h="44px"
          borderRadius="14px"
          bg="accent.tint"
          color="accent"
          align="center"
          justify="center"
        >
          <Lightbulb size={22} aria-hidden="true" />
        </Flex>
        <Heading
          as="h1"
          fontSize={{ base: '32px', md: '44px' }}
          lineHeight={1.05}
        >
          {t('site.ideas.title')}
        </Heading>
        <Text fontSize="17px" lineHeight="25px" color="ink.body">
          {t('site.ideas.intro')}
        </Text>
        {signedIn ? (
          <Button
            leftIcon={<Plus size={18} />}
            onClick={() => setSheet({ editing: null })}
          >
            {t('site.ideas.post')}
          </Button>
        ) : (
          <Text fontSize="15px" color="muted">
            {richText(t('site.ideas.signInLine'), {
              link: (
                <Link
                  as={RouterLink}
                  to="/login"
                  color="accent"
                  fontWeight={700}
                >
                  {t('site.nav.signIn')}
                </Link>
              ),
            })}
          </Text>
        )}
      </VStack>

      {!sections || ideasState.show ? (
        <LoadingPanel state={ideasState} />
      ) : (
        <VStack align="stretch" spacing={4}>
          {truncated && (
            <Text fontSize="14px" color="muted">
              {t('site.ideas.truncated', { count: IDEA_LIMITS.BOARD_ACTIVE })}
            </Text>
          )}
          {sections.active.length === 0 ? (
            <Text color="muted">
              {signedIn
                ? t('site.ideas.empty')
                : t('site.ideas.emptySignedOut')}
            </Text>
          ) : (
            <VStack as="ul" listStyleType="none" align="stretch" spacing={3}>
              {sections.active.map(card)}
            </VStack>
          )}
          <Folded
            ideas={sections.done}
            show={t('site.ideas.showDone', { count: sections.done.length })}
            hide={t('site.ideas.hideDone')}
            render={card}
          />
          <Folded
            ideas={sections.notNow}
            show={t('site.ideas.showNotNow', {
              count: sections.notNow.length,
            })}
            hide={t('site.ideas.hideNotNow')}
            render={card}
          />
        </VStack>
      )}

      {signedIn && (
        <IdeaSheet
          isOpen={sheet !== null}
          onClose={() => setSheet(null)}
          initial={initial}
          onSubmit={save}
        />
      )}
    </VStack>
  );
}

/** `/ideas`: in the app when signed in, a public page otherwise (D198). */
export function IdeasPage() {
  const signedIn = useAtomValue(isAuthenticatedAtom);
  if (!signedIn) {
    return (
      <SiteShell>
        <ReadingColumn>
          <IdeasBoard signedIn={false} />
        </ReadingColumn>
      </SiteShell>
    );
  }
  return (
    <Layout>
      {/* Pages in the app shell scroll themselves. */}
      <Box h="full" overflowY="auto">
        <Box maxW="720px" mx="auto" px={4} pt={4} pb={10}>
          <IdeasBoard signedIn />
        </Box>
      </Box>
    </Layout>
  );
}
